import { AnimatedContainer, AnimatedSection } from '@components/animated'
import { useContractAttachmentDownload } from '@services/contracts/queires/useDownloadContractAttachment'
import { viewOfferProtocol } from '@services/offers/queries/useDownloadOfferProtocol'
import { useOfferProtocols } from '@services/offers/queries/useOfferProtocols'
import {
  Eye,
  File as FileIcon,
  FileImage,
  FileText,
  type LucideIcon,
  Paperclip,
  ShieldCheck
} from 'lucide-react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ContractAttachment, ContractList } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

import { ContractWidget } from './ContractWidget'

interface DocumentsProps {
  contract?: ContractList
}

function fileIcon(name?: string) {
  const ext = name?.toLowerCase().split('.').pop()
  if (ext === 'pdf') return <FileText className="h-5 w-5 text-[#dc2626]" />
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || ''))
    return <FileImage className="h-5 w-5 text-primary" />
  return <FileIcon className="h-5 w-5 text-ink/50" />
}

function DocSection({
  icon: Icon,
  title,
  children
}: {
  icon: LucideIcon
  title: string
  children: ReactNode
}) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center gap-2.5 border-b border-ink/[0.06] px-5 py-4">
        <Icon className="h-5 w-5 text-primary" />
        <span className="font-display text-base font-bold text-ink">
          {title}
        </span>
      </div>
      <div className="divide-y divide-ink/[0.06] px-5">{children}</div>
    </Card>
  )
}

function FileRow({
  name,
  iconEl,
  badge,
  onView,
  viewLabel
}: {
  name?: string
  iconEl: ReactNode
  badge?: ReactNode
  onView: () => void
  viewLabel: string
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ink/[0.04]">
          {iconEl}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-ink" title={name}>
            {name}
          </div>
          {badge && <div className="mt-1">{badge}</div>}
        </div>
      </div>
      <Button size="sm" onClick={onView} className="shrink-0">
        <Eye className="h-4 w-4" />
        <span className="hidden sm:inline">{viewLabel}</span>
      </Button>
    </div>
  )
}

function ProtocolBadge({ tone, children }: { tone: 'green' | 'blue'; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        tone === 'green'
          ? 'bg-success-soft text-success'
          : 'bg-primary/10 text-primary'
      )}
    >
      {children}
    </span>
  )
}

const Documents = ({ contract }: DocumentsProps) => {
  const { t } = useTranslation('contract')
  const { downloadContractAttachment } = useContractAttachmentDownload()
  const { data: protocols = [] } = useOfferProtocols(contract?.offer?.id)

  const handoverProtocol = protocols.find((p) => p.protocol_type === 'HANDOVER')
  const returnProtocol = protocols.find((p) => p.protocol_type === 'RETURN')

  const handleViewProtocol = (protocolId: string) => {
    if (!contract?.offer?.id) return
    viewOfferProtocol({ offerId: contract.offer.id, protocolId })
  }

  function handleDownload(attachment_id: string, attachment_name?: string) {
    if (!contract?.id) return
    downloadContractAttachment({
      attachmentId: attachment_id,
      contractId: contract.id,
      attachment_name
    })
  }

  const attachments = contract?.attachments ?? []
  // A contract can carry both kinds at once (photos + an amendment), so group
  // by each attachment's own category.
  const amendments = attachments.filter((a) => a.category === 'AMENDMENT')
  const others = attachments.filter((a) => a.category !== 'AMENDMENT')

  const renderAttachments = (attachment: ContractAttachment, index: number) => (
    <FileRow
      key={attachment.id ?? index}
      name={attachment.name}
      iconEl={fileIcon(attachment.name)}
      viewLabel={t('contract.preview')}
      onView={() => handleDownload(String(attachment.id), attachment.name)}
    />
  )

  return (
    <AnimatedContainer staggerDelay={0.15}>
      <div className="space-y-7">
        <AnimatedSection>
          <ContractWidget contract={contract} />
        </AnimatedSection>

        {amendments.length > 0 && (
          <AnimatedSection>
            <DocSection icon={FileText} title={t('contract.amendment')}>
              {amendments.map(renderAttachments)}
            </DocSection>
          </AnimatedSection>
        )}

        {others.length > 0 && (
          <AnimatedSection>
            <DocSection icon={Paperclip} title={t('contract.other')}>
              {others.map(renderAttachments)}
            </DocSection>
          </AnimatedSection>
        )}

        {(handoverProtocol || returnProtocol) && (
          <AnimatedSection>
            <DocSection icon={ShieldCheck} title={t('contract.protocols')}>
              {handoverProtocol && (
                <FileRow
                  name={handoverProtocol.name}
                  iconEl={<FileText className="h-5 w-5 text-[#dc2626]" />}
                  viewLabel={t('contract.preview')}
                  onView={() => handleViewProtocol(handoverProtocol.id)}
                  badge={
                    <ProtocolBadge tone="green">
                      {t('contract.handover_protocol')}
                    </ProtocolBadge>
                  }
                />
              )}
              {returnProtocol && (
                <FileRow
                  name={returnProtocol.name}
                  iconEl={<FileText className="h-5 w-5 text-[#dc2626]" />}
                  viewLabel={t('contract.preview')}
                  onView={() => handleViewProtocol(returnProtocol.id)}
                  badge={
                    <ProtocolBadge tone="blue">
                      {t('contract.return_protocol')}
                    </ProtocolBadge>
                  }
                />
              )}
            </DocSection>
          </AnimatedSection>
        )}
      </div>
    </AnimatedContainer>
  )
}

export default Documents
